import { ADEQUACY_OUTCOME_ORDER } from "@app/projects/network-assessment/multi-map-export/utils/adequacyFilterOptions";
import { ExportFormatType } from "@app/exports/enumerations";
import { MAP_CONTENT_PROVIDER_OPTION_ORDER } from "@app/projects/network-assessment/multi-map-export/utils/mapContentOptions";
import { MAP_STYLE_BOUNDARY_LAYER_ORDER } from "@app/projects/network-assessment/multi-map-export/utils/mapStyleOptions";
import { ProvidersFilterOptions } from "@app/projects/network-assessment/maps/network-coverage-map/enumerators";
import {
  DEFAULT_ADEQUACY_OUTCOMES,
  DEFAULT_MAP_STYLE_BASE_MAP,
  DEFAULT_MAP_STYLE_BOUNDARY_LAYERS,
  DEFAULT_MAP_STYLE_COUNTY_SHADING,
  DEFAULT_MAP_STYLE_SHOW_SCALE_BAR,
  DEFAULT_MAP_STYLE_SOLID_FILL_COLOR,
  DEFAULT_MAP_STYLE_ZOOM_LEVEL,
  DEFAULT_OUTPUT_EMAIL_WHEN_READY,
  DEFAULT_OUTPUT_FILE_STRUCTURE,
  DEFAULT_OUTPUT_INCLUDE_PAGE_HEADER,
  MultiMapExportMapType,
  MultiMapExportState,
} from "@app/projects/network-assessment/multi-map-export/state-management/multiMapExportStateManagement";
import {
  MultiMapExportAdequacyOutcome,
  MultiMapExportBaseMap,
  MultiMapExportBoundaryLayer,
  MultiMapExportCountyShading,
  MultiMapExportFileStructure,
  MultiMapExportMemberLayer,
  MultiMapExportSolidFillColor,
  MultiMapExportZoomLevel,
} from "@app/projects/network-assessment/multi-map-export/interfaces";

/**
 * The wire values of C# `MapAdequacyStatus`. `MultiMapExportAdequacyOutcome` cannot supply them because the
 * two enums name the same four concepts differently, and an unmatched token is a 400. An enum rather than
 * bare strings so the mapping below is checked at compile time on both sides.
 *
 * `NoStandard` and `ApprovedException` are omitted: no UI control selects either.
 */
export enum MultiMapExportAdequacyStatus {
  AccessFailing = "accessFailing",
  Adequate = "adequate",
  BothFailing = "bothFailing",
  ServicingFailing = "servicingFailing",
}

/**
 * The pairs match by meaning, not by name: an outcome of "failing the servicing requirement" — access met,
 * servicing not — is `ServicingFailing`.
 *
 * Typed as a complete `Record` so adding an outcome to the TypeScript enum is a compile error here rather
 * than an unmatched token and a 400 at export time.
 */
const ADEQUACY_STATUS_BY_OUTCOME: Record<MultiMapExportAdequacyOutcome, MultiMapExportAdequacyStatus> = {
  [MultiMapExportAdequacyOutcome.FailingAccessRequirement]: MultiMapExportAdequacyStatus.AccessFailing,
  [MultiMapExportAdequacyOutcome.FailingBothRequirements]: MultiMapExportAdequacyStatus.BothFailing,
  [MultiMapExportAdequacyOutcome.FailingServicingRequirement]: MultiMapExportAdequacyStatus.ServicingFailing,
  [MultiMapExportAdequacyOutcome.Passing]: MultiMapExportAdequacyStatus.Adequate,
};

const FORMAT_TYPE_BY_FILE_STRUCTURE: Record<MultiMapExportFileStructure, ExportFormatType> = {
  [MultiMapExportFileStructure.CollatedPdf]: ExportFormatType.Pdf,
  [MultiMapExportFileStructure.IndividualJpegs]: ExportFormatType.Jpeg,
  [MultiMapExportFileStructure.IndividualPngs]: ExportFormatType.Png,
};

/**
 * Request body for `POST v2.0/exports/multi-map-export`.
 *
 * Enum-valued fields travel as their lowerCamel string values, because the API host registers Newtonsoft
 * with a `StringEnumConverter(CamelCaseNamingStrategy)` and they bind to the C# members by name. The names
 * therefore have to match: `ExportsController` is an `[ApiController]` with no
 * `SuppressModelStateInvalidFilter`, so a value with no matching member is a 400, not a silent default.
 *
 * This interface is here rather than in `interfaces.ts` because it references `MultiMapExportMapType` from
 * `state-management`, and `state-management` imports `interfaces`, so declaring it there would be an
 * `import/no-cycle` error.
 */
export interface MultiMapExportRequestModel {
  /** Adequacy Filter outcomes translated to the C# member names — see `ADEQUACY_STATUS_BY_OUTCOME`. */
  readonly adequacyStatuses: readonly MultiMapExportAdequacyStatus[];
  /** Boundary lines and their labels as one list of enabled layers, as the Map Style section stores them. */
  readonly boundaryLayers: readonly MultiMapExportBoundaryLayer[];
  readonly countyShading: MultiMapExportCountyShading;
  readonly countySsas: readonly string[];
  readonly customProjectId: number;
  /**
   * The Output section's "Email me when the report is ready". Named for the C#
   * `ExportRequestBaseModel.EmailOnSuccess` it binds to, not for the `emailWhenReady` state field it comes
   * from: the export pipeline gates every report-complete email on that property.
   */
  readonly emailOnSuccess: boolean;
  readonly exportName: string;
  /** Also shown as the Format in the Reports list. */
  readonly formatType: ExportFormatType;
  /**
   * The Output section's "Page header on each map". Named for the C#
   * `MultiMapExportRequestModel.IncludeDetailsBox` it binds to, not for the `includePageHeader` state field
   * it comes from: the server property covers the summary panel and the stat row, which is what unchecking
   * the box drops. The title, project and date rows are kept either way.
   */
  readonly includeDetailsBox: boolean;
  readonly includeMembersWithAccess: boolean;
  readonly includeMembersWithoutAccess: boolean;
  /** Only used when `countyShading` is No Shading; the other modes draw no base map. */
  readonly mapBasemapStyle: MultiMapExportBaseMap;
  readonly mapRenderType: MultiMapExportMapType;
  readonly mapZoomLevel: MultiMapExportZoomLevel;
  readonly projectRunConfigurationId: number;
  /** Leaf provider options only — parent checkboxes are never stored, so they can never be sent. */
  readonly providerLayers: readonly ProvidersFilterOptions[];
  readonly runAsynchronous: boolean;
  readonly sharedUsers: readonly number[];
  readonly showScaleBar: boolean;
  /** Only used when `countyShading` is Solid Color. */
  readonly solidFillColor: MultiMapExportSolidFillColor;
  readonly specialtyGroupCodes: readonly string[];
}

export interface MultiMapExportRequestContext {
  readonly customProjectId: number;
  readonly projectRunConfigurationId: number;
  readonly reportName: string;
}

/**
 * Orders a selection by a canonical option list, which both sorts it and drops anything not in that list.
 * The ordering keeps the payload independent of the order the user clicked; the filtering stops a parent
 * checkbox value or an older `ProvidersFilterOptions` member reaching a C# enum with no such member, which
 * would be a 400 rather than an ignored field.
 */
const inCanonicalOrder = <TValue>(canonical: readonly TValue[], selected?: readonly TValue[]): TValue[] => {
  const chosen = new Set(selected ?? []);

  return canonical.filter((value) => chosen.has(value));
};

/**
 * Flattens the configuration sections into the export request.
 *
 * Every collection is sent explicitly, never collapsed to `[]` to mean "all". On the server an empty
 * collection means every county in the canonical service area, which is not the set the user was shown:
 * this page fetched the project's own region sets, dropped `Unknown`-status counties and applied the
 * County Status pills. Collapsing "all selected" would quietly widen the export.
 */
export const buildMultiMapExportRequest = (
  context: MultiMapExportRequestContext,
  selections?: MultiMapExportState,
): MultiMapExportRequestModel => {
  const memberLayers = selections?.selectedMapContentMemberLayers ?? [];

  return {
    // Defaulted rather than sent empty: the Adequacy Filter shows the Failing outcomes already checked
    // before any interaction, and on the server an empty list means no adequacy filtering at all.
    adequacyStatuses: inCanonicalOrder(ADEQUACY_OUTCOME_ORDER, selections?.selectedAdequacyOutcomes ?? DEFAULT_ADEQUACY_OUTCOMES)
      .map((outcome) => ADEQUACY_STATUS_BY_OUTCOME[outcome]),
    boundaryLayers: inCanonicalOrder(MAP_STYLE_BOUNDARY_LAYER_ORDER, selections?.selectedBoundaryLayers ?? DEFAULT_MAP_STYLE_BOUNDARY_LAYERS),
    countyShading: selections?.selectedCountyShading ?? DEFAULT_MAP_STYLE_COUNTY_SHADING,
    countySsas: [...(selections?.selectedCountySsas ?? [])],
    customProjectId: context.customProjectId,
    emailOnSuccess: selections?.emailWhenReady ?? DEFAULT_OUTPUT_EMAIL_WHEN_READY,
    exportName: context.reportName,
    formatType: FORMAT_TYPE_BY_FILE_STRUCTURE[selections?.selectedFileStructure ?? DEFAULT_OUTPUT_FILE_STRUCTURE],
    includeDetailsBox: selections?.includePageHeader ?? DEFAULT_OUTPUT_INCLUDE_PAGE_HEADER,
    includeMembersWithAccess: memberLayers.includes(MultiMapExportMemberLayer.WithAccess),
    includeMembersWithoutAccess: memberLayers.includes(MultiMapExportMemberLayer.WithoutAccess),
    mapBasemapStyle: selections?.selectedBaseMap ?? DEFAULT_MAP_STYLE_BASE_MAP,
    mapRenderType: selections?.selectedMapType ?? MultiMapExportMapType.CountySpecialtyDetail,
    mapZoomLevel: selections?.selectedZoomLevel ?? DEFAULT_MAP_STYLE_ZOOM_LEVEL,
    projectRunConfigurationId: context.projectRunConfigurationId,
    providerLayers: inCanonicalOrder(MAP_CONTENT_PROVIDER_OPTION_ORDER, selections?.selectedMapContentProviderOptions),
    runAsynchronous: true,
    sharedUsers: [...(selections?.sharedUsers ?? [])],
    showScaleBar: selections?.showScaleBar ?? DEFAULT_MAP_STYLE_SHOW_SCALE_BAR,
    solidFillColor: selections?.selectedSolidFillColor ?? DEFAULT_MAP_STYLE_SOLID_FILL_COLOR,
    specialtyGroupCodes: [...(selections?.selectedSpecialtyGroupCodes ?? [])],
  };
};
